import { Button } from "@progress/kendo-react-buttons";
import {
  InputSeparator,
  InputSuffix,
  TextBox,
  TextBoxChangeEvent,
} from "@progress/kendo-react-inputs";
import { Hint } from "@progress/kendo-react-labels";
import axios, { AxiosError } from "axios";
import { MAX_LENGTH_OPTION } from "components/elements/ElementEditor";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useConfirmModal from "hooks/useConfirmModal";
import { cloneDeep } from "lodash";
import * as React from "react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { selectAppConfig } from "store/appConfigSlice";
import { ConfirmModalProps } from "store/confirmModalSlice";
import { showErrorMessage } from "store/errorModalSlice";
import {
  selectVendorResponse,
  setVendorAttachments,
} from "store/vendorResponseSlice";
import { Attachment, AttachmentType } from "types/RfiTypes";

interface IRfiVendorAttachmentProps {
  attachments: Attachment[];
}

const VendorEditAttachments = (props: IRfiVendorAttachmentProps) => {
  // Hooks
  const dispatch = useAppDispatch();
  const vendorResponse = useAppSelector(selectVendorResponse);
  const { open } = useConfirmModal();
  const appConfig = useAppSelector(selectAppConfig);

  // State
  const [localAttachments, setLocalAttachments] = React.useState<
    Array<Attachment>
  >(
    vendorResponse?.attachments
      ? cloneDeep(vendorResponse?.attachments)
      : new Array<Attachment>()
  );

  //Effects
  useEffect(() => {
    setLocalAttachments(
      vendorResponse?.attachments
        ? cloneDeep(vendorResponse?.attachments)
        : new Array<Attachment>()
    );
  }, [vendorResponse?.attachments]);

  // Functions
  const swapAttachments = (index1: number, index2: number) => {
    var att = cloneDeep(localAttachments);
    const sequence1 = att[index1].sequence;
    const sequence2 = att[index2].sequence;

    var temp = att[index1];

    //swap values and reset sequences
    att[index1] = att[index2];
    att[index1].sequence = sequence1;

    att[index2] = temp;
    att[index2].sequence = sequence2;

    return att;
  };

  // Handlers
  const handleUpdateTitleValue = (event: TextBoxChangeEvent, id: number) => {
    setLocalAttachments(
      localAttachments.map((attachment: Attachment, index: number) => {
        if (attachment.id === id) {
          attachment.title = event.target.value?.toString();
        }
        return attachment;
      })
    );
  };

  const handleUpdateTitle = async (
    rfiId: number,
    id: number,
    value: string
  ) => {
    let updateParams = {
      id: id,
      rfiId: rfiId,
      title: value,
      attachmentType: AttachmentType.VENDOR_RESPONSE,
      vendorResponseId: vendorResponse.vendorResponse?.vendorResponseId ?? null,
    };

    return axios
      .put(`/api/v1/rfi/${rfiId}/Attachments`, updateParams)
      .then(() => {
        dispatch(setVendorAttachments(localAttachments));
        toast.success("Updated");
      })
      .catch((error:  Error | AxiosError) => {
        console.log(error);
        dispatch(
          showErrorMessage(`There was an error updating file title - ${error}`)
        );
      });
  };

  const handleMoveFile = async (
    rfiId: number,
    sequence: number,
    direction: number
  ) => {
    let moveParams = {
      rfiId: rfiId,
      sequence: sequence,
      direction: direction,
      attachmentType: AttachmentType.VENDOR_RESPONSE,
      vendorResponseId: vendorResponse.vendorResponse?.vendorResponseId ?? null,
    };

    return axios
      .put(`/api/v1/rfi/${rfiId}/Attachments/move`, moveParams)
      .catch((error:  Error | AxiosError) => {
        console.log(error);
        dispatch(
          showErrorMessage(`There was an error updating file title - ${error}`)
        );
      });
  };

  const handleDeleteFile = async (rfiId: number, id: number) => {
    const isConfirmed = await open({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this attachment?",
      description: "Confirm delete attachment",
    } as ConfirmModalProps);

    if (isConfirmed) {
      await axios
        .delete(`/api/v1/rfi/${rfiId}/Attachments/${id}`, {
          params: {
            vendorResponseId: vendorResponse?.vendorResponse?.vendorResponseId,
          },
        })
        .then(() => {
          const newAtt = localAttachments.filter(
            (file: Attachment) => file.id !== id
          );
          newAtt.map((attachment, index) => {
            attachment.sequence = index + 1;
          });

          dispatch(setVendorAttachments(newAtt));
        })
        .catch((error:  Error | AxiosError) => {
          console.log(error);
          dispatch(
            showErrorMessage(
              `There was an error updating file title - ${error}`
            )
          );
        });
    }
  };

  const handleMoveFileDown = async (rfiId: number, index: number) => {
    await handleMoveFile(rfiId, index, 1).then(() => {
      const newAtt = swapAttachments(index - 1, index);
      //setAttachments(newAtt);
      dispatch(setVendorAttachments(newAtt));
    });
  };

  const handleMoveFileUp = async (rfiId: number, index: number) => {
    await handleMoveFile(rfiId, index, 0).then(() => {
      const newAtt = swapAttachments(index - 1, index - 2);
      //setAttachments(newAtt);
      dispatch(setVendorAttachments(newAtt));
    });
  };

  // Render
  return (
    <div className="container-fluid">
      {localAttachments.map((file: Attachment, index: number) => {
        return (
          <div
            className="d-flex flex-row flex-nowrap my-1"
            key={`file-${index}`}
          >
            <div
              className="flex-nowrap"
              style={{
                display: "block-inline",
                width: "55%",
                marginRight: "10px",
              }}
            >
              <label className="visuallyHidden" htmlFor={`file-${file.id}`}>
                {`File ${file.id} ${file.title} (${file.filename})`}
              </label>
              <TextBox
                id={`file-${file.id}`}
                //className="form-control col"
                type="text"
                value={file.title ?? ""}
                placeholder={"Document title"}
                maxLength={appConfig.maxOptionLength ?? MAX_LENGTH_OPTION}
                onChange={(event: TextBoxChangeEvent) =>
                  handleUpdateTitleValue(event, file.id)
                }
                suffix={() => (
                  <React.Fragment>
                    <InputSeparator />
                    <InputSuffix>
                      <Button
                        themeColor="primary"
                        fillMode={"flat"}
                        rounded={null}
                        onClick={() =>
                          handleUpdateTitle(
                            file.rfiId ?? 0,
                            file.id,
                            file.title ?? ""
                          )
                        }
                      >
                        Update
                      </Button>
                    </InputSuffix>
                  </React.Fragment>
                )}
              />
              <Hint
                direction={"end"}
                className="col-1 align-items-center"
                style={{ minWidth: "50px" }}
              >
                {file.title?.length ?? 0} /{" "}
                {appConfig.maxOptionLength ?? MAX_LENGTH_OPTION}
              </Hint>
            </div>
            <div style={{ width: "35%", marginRight: "10px" }}>
              (
              <Link
                to={`/api/v1/rfi/${file.rfiId}/Attachments/${file.attachmentLink}`}
                target="_blank"
                download
              >
                {`${file.filename}`}
              </Link>
              )
            </div>
            <div
              className="d-flex flex-row flex-nowrap my-1"
              style={{ width: "100px" }}
            >
              {
                // Don't show Down Arrow on last item

                <div className="k-mr-2" style={{ textAlign: "center" }}>
                  <button
                    disabled={
                      !(localAttachments.length > 0
                        ? file !== localAttachments[localAttachments.length - 1]
                        : false)
                    }
                    className="text-align-center round-button"
                    aria-label={`Move file ${file.filename} Down`}
                    onClick={() =>
                      handleMoveFileDown(file.rfiId ?? 0, file.sequence ?? 0)
                    }
                  >
                    <i className="fa-solid fa-arrow-down" />
                  </button>
                </div>
              }
              {
                // Don't show Up Arrow on first item
                (
                  localAttachments.length > 0
                    ? file !== localAttachments[0]
                    : false
                ) ? (
                  <div className="k-mr-2" style={{ textAlign: "center" }}>
                    <button
                      className="text-align-center round-button"
                      //style={{ textAlign: "center" }}
                      aria-label={`Move Option ${file.filename} Up`}
                      onClick={() =>
                        handleMoveFileUp(file.rfiId ?? 0, file.sequence ?? 0)
                      }
                    >
                      <i className="fa-solid fa-arrow-up" />
                    </button>
                  </div>
                ) : (
                  <div className="k-mr-2" style={{ textAlign: "center" }}>
                    <button
                      disabled
                      className="text-align-center round-button"
                      //style={{ textAlign: "center" }}
                      aria-label={`Move Option ${file.filename} Up`}
                      onClick={() =>
                        handleMoveFileUp(file.rfiId ?? 0, file.sequence ?? 0)
                      }
                    >
                      <i className="fa-solid fa-arrow-up" />
                    </button>
                  </div>
                )
              }
              <div className="k-mr-2" style={{ textAlign: "center" }}>
                <button
                  className="align-items-center round-button"
                  aria-label={`Delete file ${file.filename}`}
                  onClick={() => handleDeleteFile(file.rfiId ?? 0, file.id)}
                >
                  <i className="fa-solid fa-trash" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VendorEditAttachments;
